import type { StoreApi } from "zustand";

import * as React from "react";
import { useCallback, useContext, useRef } from "react";

import { EditorStoreContext } from "@agent-ui/store/editorStore";
import { useIsMobile } from "@replo/design-system/hooks/use-mobile";
import { useSearchParams } from "react-router-dom";
import { createContext, useRequiredContext } from "replo-utils/react/context";
import { createStore } from "zustand";
import { shallow } from "zustand/shallow";
import { useStoreWithEqualityFn } from "zustand/traditional";

/**
 * NOTE (Yuxin, 2026-01-25): Simple store for sandbox iframe navigation.
 * When OpenCode creates a new page, the chat can update this store
 * and the PageRenderer will navigate the iframe to the new route.
 *
 * NOTE (Gabe, 2026-03-06): The store is scoped to a project via
 * SandboxNavigationProvider (keyed by projectId). React lifecycle
 * handles reset automatically on project switch.
 */

interface SandboxNavigationState {
  // The route to navigate to (e.g., "/banana/5")
  overrideRoute: string | null;

  // Timestamp when the override was set (used to force re-renders)
  overrideTimestamp: number;

  // Chat session URL from the active streaming session.
  chatPreviewUrl: string | null;

  // Actions
  navigateTo: (route: string) => void;
  clearOverride: () => void;
  setChatPreviewUrl: (url: string | null) => void;
}

function createSandboxNavigationStore() {
  return createStore<SandboxNavigationState>((set) => ({
    overrideRoute: null,
    overrideTimestamp: 0,
    chatPreviewUrl: null,

    navigateTo: (route: string) => {
      set({
        overrideRoute: route,
        overrideTimestamp: Date.now(),
      });
    },

    clearOverride: () => {
      set({
        overrideRoute: null,
        overrideTimestamp: 0,
      });
    },

    setChatPreviewUrl: (url: string | null) => {
      set({ chatPreviewUrl: url });
    },
  }));
}

type SandboxNavigationStore = StoreApi<SandboxNavigationState>;

const SandboxNavigationContext = createContext<SandboxNavigationStore | null>(
  "SandboxNavigationContext",
  null,
);

export function SandboxNavigationProvider({
  children,
}: React.PropsWithChildren) {
  const storeRef = useRef<SandboxNavigationStore | undefined>(undefined);
  if (!storeRef.current) {
    storeRef.current = createSandboxNavigationStore();
  }

  return (
    <SandboxNavigationContext.Provider value={storeRef.current}>
      {children}
    </SandboxNavigationContext.Provider>
  );
}

function useSandboxNavigationStore<T>(
  selector: (state: SandboxNavigationState) => T,
): T {
  const store = useRequiredContext(SandboxNavigationContext);
  return useStoreWithEqualityFn(store, selector, shallow);
}

export const useSandboxOverrideRoute = () => {
  return useSandboxNavigationStore((state) => state.overrideRoute);
};

export const useSandboxNavigate = () => {
  const navigateTo = useSandboxNavigationStore((state) => state.navigateTo);
  const editorStoreContext = useContext(EditorStoreContext);
  const isMobile = useIsMobile();
  const [, setSearchParams] = useSearchParams();

  return useCallback(
    (route: string) => {
      navigateTo(route);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("pageRoute", route);
        return next;
      });
      if (isMobile) {
        editorStoreContext?.store
          .getState()
          .actions.setMobileChatMode("preview");
      }
    },
    [navigateTo, setSearchParams, editorStoreContext, isMobile],
  );
};

export const useSandboxClearOverride = () => {
  return useSandboxNavigationStore((state) => state.clearOverride);
};

export const useSandboxPreviewBaseUrl = () => {
  return useSandboxNavigationStore((state) => state.chatPreviewUrl);
};

export const useSetChatPreviewUrl = () => {
  return useSandboxNavigationStore((state) => state.setChatPreviewUrl);
};
